/** Impulse-response style measurements on rendered audio (used by tests and the debug hooks). */

export interface IrMeasure {
  /** Seconds until the first sample above -40 dB (re peak). */
  firstArrival: number
  /** Seconds between the direct arrival and the first clearly separate echo (0 if none found). */
  firstEchoDelay: number
  /** Reverberation time extrapolated from the -5…-25 dB part of the Schroeder decay curve (seconds). */
  rt60: number
  peak: number
}

function energyEnvelope(mono: Float32Array, win: number): Float32Array {
  const n = Math.floor(mono.length / win)
  const env = new Float32Array(n)
  for (let i = 0; i < n; i++) {
    let s = 0
    for (let j = 0; j < win; j++) s += mono[i * win + j]! ** 2
    env[i] = s / win
  }
  return env
}

export function measureIr(left: Float32Array, right: Float32Array, sampleRate: number): IrMeasure {
  const n = Math.min(left.length, right.length)
  const mono = new Float32Array(n)
  let peak = 0
  for (let i = 0; i < n; i++) {
    mono[i] = left[i]! + right[i]!
    peak = Math.max(peak, Math.abs(mono[i]!))
  }
  if (peak === 0) return { firstArrival: 0, firstEchoDelay: 0, rt60: 0, peak: 0 }

  const thr = peak * 0.01 // -40 dB
  let first = 0
  while (first < n && Math.abs(mono[first]!) < thr) first++

  // Echo detection on a 1 ms energy envelope.
  const win = Math.max(1, Math.round(sampleRate * 0.001))
  const env = energyEnvelope(mono, win)
  const firstWin = Math.floor(first / win)
  let direct = 0
  for (let i = firstWin; i < Math.min(env.length, firstWin + 8); i++) direct = Math.max(direct, env[i]!)
  let echo = 0
  let i = firstWin + 8
  // skip the direct sound's own decay: wait until the envelope drops below 1% of the direct peak
  while (i < env.length && env[i]! > direct * 0.01) i++
  for (; i < env.length - 2; i++) {
    const isPeak = env[i]! > env[i - 1]! && env[i]! >= env[i + 1]!
    if (isPeak && env[i]! > direct * 0.0006) {
      echo = ((i - firstWin) * win) / sampleRate
      break
    }
  }

  // Schroeder backward integration → decay curve in dB.
  const total = new Float64Array(env.length + 1)
  for (let k = env.length - 1; k >= 0; k--) total[k] = total[k + 1]! + env[k]!
  const t0 = total[firstWin]! || 1
  const curve = (k: number) => 10 * Math.log10(Math.max(total[k]!, 1e-30) / t0)
  let k5 = firstWin
  while (k5 < env.length && curve(k5) > -5) k5++
  let k25 = k5
  while (k25 < env.length && curve(k25) > -25) k25++
  const rt60 = k25 < env.length ? (((k25 - k5) * win) / sampleRate) * 3 : 0

  return { firstArrival: first / sampleRate, firstEchoDelay: echo, rt60, peak }
}
